/**
 * A classroom in a list, as a card.
 *
 * The shape is the one a reader already knows from the rest of the app: a feed post
 * is a hairline-bordered box whose picture runs to the edges, and a module in a grid
 * is the same box with its own facts. So a classroom is that box — **cover band,
 * name, who runs it, what it is, the two numbers worth knowing** — and nothing else
 * invented for it.
 *
 * Three choices are deliberate:
 *
 *  - **The cover keeps the room's own band.** 3:1, exactly the shape the editor
 *    crops and the classroom's page draws, so the picture a school chose is the
 *    picture a reader sees here — not a slice of its middle. The card used to crop it
 *    into an 88px strip, which is a different picture at every screen width.
 *  - **Two facts, not four.** A reader choosing a room asks *is anybody in it* and
 *    *does anything happen in it*, so the card says students and posts and leaves
 *    teachers and parents to the room's own metrics row, one press away. Four counts
 *    in a row is a spreadsheet; the card is a door.
 *  - **The cover a room has not chosen is still a cover.** A classroom without a
 *    picture gets the gradient it starts life with, with its own glyph faintly on
 *    top, so it reads as *a room without a photo* rather than as a broken image. The
 *    room's page keeps its band plain — there the name sits right under it.
 *
 * Everything that describes the room — public or private, its interests — is one
 * line of small print at the bottom, because it is the fine print: true, useful
 * when looked for, and not what anybody scans first.
 */
import {View} from 'react-native'
import {Image} from 'expo-image'
import {plural} from '@lingui/core/macro'
import {useLingui} from '@lingui/react/macro'

import {gradeLabel} from '#/lib/curriculum'
import {sanitizeHandle} from '#/lib/strings/handles'
import {
  type Classroom,
  classroomLevelName,
  classroomRoute,
  useClassroomActor,
} from '#/state/classrooms'
import {atoms as a, useTheme, utils} from '#/alf'
import {Chip} from '#/components/Chip'
import {Circle_And_Square_Stroke1_Corner0_Rounded_Filled as ClassroomIcon} from '#/components/icons/CircleAndSquare'
import {Group3_Stroke2_Corner0_Rounded as StudentsIcon} from '#/components/icons/Group'
import {PageText_Stroke2_Corner0_Rounded as PostsIcon} from '#/components/icons/PageText'
import {LinearGradientBackground} from '#/components/LinearGradientBackground'
import {Link} from '#/components/Link'
import {Text} from '#/components/Typography'

export function ClassroomCard({
  classroom,
  joined = false,
}: {
  classroom: Classroom
  /** Whether the reader holds a seat in it; the server's list says so, not the room. */
  joined?: boolean
}) {
  const t = useTheme()
  const {t: l} = useLingui()
  const actor = useClassroomActor()
  const isOwner = classroom.ownerDid === actor?.did
  const isMember = isOwner || joined
  const students = classroom.publishedCounts?.students ?? 0
  const cover = classroom.coverUrl ?? classroom.cover
  const posts = classroom.contentCount ?? 0

  /*
   * The fine print, in the order a reader asks it: what kind of room it is, who can
   * find it, whether its levels are the school's own names, and what it is about.
   * Only two interests get to speak — the rest are one press away in the room, and a
   * line that wraps has stopped being fine print.
   */
  const finePrint = [
    l`Classroom`,
    classroom.visibility === 'private' ? l`Private` : l`Public`,
    classroom.levelNameType === 'SYSTEM' ? l`Own level names` : null,
    /* A room that takes every country says so, where its audience is read. */
    classroom.international ? l`International` : null,
    ...classroom.interests.slice(0, 2),
  ].filter(Boolean)

  return (
    <Link
      /*
        The same address the copy button hands out (`classroomRoute`): the
        school's handle, then the room's.
      */
      to={classroomRoute(classroom)}
      label={l`Open ${classroom.name}`}
      style={[a.w_full]}>
      {/*
        `w_full` is load-bearing: the link is a *row* flex container, and a card in a
        row is as wide as its contents want to be — which for a cover is the picture's
        own width, so two classrooms came out different sizes on the same screen. A
        card is the width of the column it is in.
      */}
      <View
        style={[
          a.w_full,
          a.rounded_lg,
          a.overflow_hidden,
          a.border,
          t.atoms.border_contrast_low,
          t.atoms.bg,
        ]}>
        <View style={[styles.cover, t.atoms.bg_contrast_25]}>
          {cover ? (
            <Image
              source={{uri: cover}}
              style={[a.w_full, a.h_full]}
              contentFit="cover"
              accessibilityIgnoresInvertColors
            />
          ) : (
            <LinearGradientBackground
              gradient="sky"
              style={[a.w_full, a.h_full, a.align_center, a.justify_center]}>
              <ClassroomIcon
                size="4xl"
                fill={utils.alpha(t.palette.white, 0.55)}
              />
            </LinearGradientBackground>
          )}
        </View>

        {/*
          The name and its byline are one block — they are the same thought, read
          twice — so they sit closer to each other than to anything below them.
        */}
        <View style={[a.p_md, a.gap_sm]}>
          <View style={[a.gap_xs]}>
            <View style={[a.flex_row, a.align_start, a.gap_sm]}>
              <Text
                emoji
                numberOfLines={2}
                style={[
                  a.text_lg,
                  a.font_bold,
                  a.flex_1,
                  a.leading_tight,
                  t.atoms.text,
                ]}>
                {classroom.name}
              </Text>
              {isMember ? (
                <Badge
                  label={isOwner ? l`Your classroom` : l`Joined`}
                  owner={isOwner}
                />
              ) : null}
            </View>

            <Text
              numberOfLines={1}
              style={[a.text_sm, t.atoms.text_contrast_medium]}>
              {/*
                The school, then what the room teaches: its level and, when the
                level has years, the year itself. A classroom with no level says
                only who runs it — there is no level to print, and an empty line
                between two dots would read as one that went missing.
              */}
              {[
                classroom.ownerName ??
                  sanitizeHandle(classroom.ownerHandle, '@'),
                /* The room's own name for its level, where it gave one. */
                classroom.level ? classroomLevelName(classroom) : null,
                classroom.grade
                  ? l(gradeLabel(classroom.grade) ?? {message: classroom.grade})
                  : null,
              ]
                .filter(Boolean)
                .join(' · ')}
            </Text>
          </View>

          {classroom.description ? (
            <Text
              emoji
              numberOfLines={2}
              style={[a.text_sm, a.leading_snug, t.atoms.text_contrast_high]}>
              {classroom.description}
            </Text>
          ) : null}

          <View style={[a.flex_row, a.align_center, a.flex_wrap, a.gap_xs]}>
            <Chip
              accent
              icon={<StudentsIcon size="xs" fill={t.palette.primary_500} />}
              label={plural(students, {
                one: '# student',
                other: '# students',
              })}
            />
            <Chip
              icon={
                <PostsIcon
                  size="xs"
                  fill={t.atoms.text_contrast_medium.color}
                />
              }
              label={plural(posts, {one: '# post', other: '# posts'})}
            />
          </View>

          <Text
            numberOfLines={1}
            style={[a.text_xs, t.atoms.text_contrast_medium]}>
            {finePrint.join(' · ')}
          </Text>
        </View>
      </View>
    </Link>
  )
}

/**
 * Whether this room is one of yours, said quietly.
 *
 * It used to be the app's blue, on a card whose subject is the room rather than the
 * reader's relationship to it — a badge louder than the name it sat beside. Tinted,
 * it answers the question and gets out of the way.
 */
function Badge({label, owner}: {label: string; owner: boolean}) {
  const t = useTheme()
  return (
    <View
      style={[
        styles.badge,
        owner
          ? {backgroundColor: t.palette.primary_50}
          : t.atoms.bg_contrast_100,
      ]}>
      <Text
        numberOfLines={1}
        style={[
          a.text_xs,
          a.font_semi_bold,
          owner ? {color: t.palette.primary_500} : t.atoms.text_contrast_high,
        ]}>
        {label}
      </Text>
    </View>
  )
}

const styles = {
  /** The room's own band, at the room's own ratio. */
  cover: {
    width: '100%',
    aspectRatio: 3,
  },
  badge: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 999,
  },
} as const
